import {
  ExpensePhase,
  itemsReconciliation,
  sumMoney,
  type ExpenseItemWithRelations,
  type ItemsReconciliation,
  type PlanningItem,
} from '@peles/shared';

/** What the grid groups rows by; `none` is the list as ordered (and reorderable). */
export type ItemsGroupBy = 'none' | 'need' | 'vendor' | 'room' | 'category';

export const ITEMS_GROUP_LABELS: Record<ItemsGroupBy, string> = {
  none: 'No grouping',
  need: 'Need',
  vendor: 'Vendor',
  room: 'Room',
  category: 'Category',
};

export interface ItemsGroup {
  key: string;
  label: string;
  items: PlanningItem[];
  subtotal: number;
  /**
   * Grouped by need: how all its items — not only the rows on screen — stand
   * against its budget or price.
   */
  reconciliation: ItemsReconciliation | null;
  /** Grouped by need: the line itself. */
  line: ExpenseItemWithRelations | null;
}

/** An item's name as the grid shows it. */
export function itemLabel(item: PlanningItem): string {
  return item.itemName ?? item.catalogItemName ?? '—';
}

/** A need's name: its own, else its category's. */
export function lineName(
  line: Pick<ExpenseItemWithRelations, 'itemName' | 'vendorCategoryName'>,
): string {
  return line.itemName?.trim() || line.vendorCategoryName || 'Need';
}

/**
 * How the items, in the order given, fall into groups — each with the
 * subtotal of the rows given, and for a need what **all** its items say
 * against its budget or price: `allItems` is every item of the event, since a
 * filter or the page limit leaves some of a need's items out of `items`, and
 * "Use as budget" / "Use as price" must never write a part of the total.
 * Groups keep the order their first item has; "no …" groups go last.
 */
export function groupItems(
  items: PlanningItem[],
  by: ItemsGroupBy,
  lines: ExpenseItemWithRelations[],
  allItems: PlanningItem[] = items,
): ItemsGroup[] {
  if (by === 'none') {
    return [
      {
        key: 'all',
        label: '',
        items,
        subtotal: sumMoney(items.map((i) => i.total)),
        reconciliation: null,
        line: null,
      },
    ];
  }
  const lineById = new Map(lines.map((l) => [l.id, l]));
  const keyOf = (item: PlanningItem): [string, string] => {
    switch (by) {
      case 'need': {
        const line = item.expenseItemId ? lineById.get(item.expenseItemId) : undefined;
        if (!item.expenseItemId) return ['', 'Needs a line'];
        return [item.expenseItemId, line ? lineName(line) : (item.expenseItemName ?? 'Need')];
      }
      case 'vendor': {
        const key = item.expenseVendorId ?? item.expenseVendorName ?? '';
        return [key, item.expenseVendorName ?? 'No vendor yet'];
      }
      case 'room':
        return [item.roomId ?? '', item.roomName ?? 'No room'];
      case 'category':
        return [item.vendorCategoryId ?? '', item.vendorCategoryName ?? 'No category'];
    }
  };
  const groups = new Map<string, ItemsGroup>();
  for (const item of items) {
    const [key, label] = keyOf(item);
    let group = groups.get(key);
    if (!group) {
      group = { key, label, items: [], subtotal: 0, reconciliation: null, line: null };
      groups.set(key, group);
    }
    group.items.push(item);
  }
  const result = [...groups.values()];
  const needTotal = (lineId: string) =>
    sumMoney(allItems.filter((i) => i.expenseItemId === lineId).map((i) => i.total));
  for (const group of result) {
    group.subtotal = sumMoney(group.items.map((i) => i.total));
    if (by === 'need' && group.key) {
      const line = lineById.get(group.key) ?? null;
      group.line = line;
      if (line) {
        group.reconciliation = itemsReconciliation({
          booked: line.phase === ExpensePhase.BOOKED,
          itemsTotal: needTotal(line.id),
          budget: line.budgetAmount,
          price: line.price,
        });
      }
    }
  }
  // The groups with no key — no line, vendor, room or category — go last.
  return [...result.filter((g) => g.key), ...result.filter((g) => !g.key)];
}
